import { memo, useCallback, useState } from 'react'

import { Button, DialogActions, DialogContent, DialogContentText, Divider } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { DialogTitle } from '@shared/components/MaterialUIEnhancements'
import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { useAvcInstanceDeleteMutation } from 'hooks/mutations/content'
import { avcInstancesRoute } from 'pages/NWPlatform/AvcSystem/Instances'
import { avcInstanceRoute } from 'pages/NWPlatform/AvcSystem/Instances/Instance'
import { avcInstanceQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
  const [successDialogOpen, setSuccessDialogOpen] = useState(false)

  const { environmentId, avcInstanceId: instanceId } = avcInstanceRoute.useParams()
  const navigate = useNavigate({ from: avcInstanceRoute.fullPath })

  const instanceQuery = useQuery(avcInstanceQueryKeys.detail({ instanceId }))
  const { data: instance } = instanceQuery

  const { mutate: deleteEvent } = useAvcInstanceDeleteMutation()

  const handleDeletion = useCallback(() => {
    setAnchorEl(undefined)

    deleteEvent(
      { instanceId },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:ContentProcessing.Instance.nameDelete', { name: instance?.name }), {
            variant: 'success',
          })

          setTimeout(() => {
            navigate({ to: avcInstancesRoute.to, params: { environmentId } })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [deleteEvent, enqueueSnackbar, environmentId, instance, instanceId, navigate, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />

      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={instance?.name}
        entity={t('entity:content.avcInstance', { count: 1 })}
      >
        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={instanceId} />
        <Divider sx={{ my: 1 }} component="li" />
        <MenuItemDelete onConfirm={handleDeletion} />
      </ActionMenu>
      <Dialog
        open={successDialogOpen}
        onClose={() => {
          setSuccessDialogOpen(false)
        }}
      >
        <DialogTitle>{t('domain:ContentProcessing.Instance.newAccessKey')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('domain:ContentProcessing.Instance.newKeyMessage')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessDialogOpen(false)}>{t('gotIt')}</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
